import React, { useMemo, useState, Suspense } from 'react';
import { ParcelDimsData } from '../../types/parcel';
import './SidePanel.css';
import ZoneInfo from '../ZoneInfo/ZoneInfo';
import TabNavigation from './TabNavigation';
import { useAuth } from '../../context/AuthContext';

const AIAnalysis = React.lazy(() => import('../AIAnalysis/AIAnalysis'));

interface SidePanelProps {
  selectedZone: any;
  userRole: string;
  parcelData: any;
  detailedParcelData: ParcelDimsData | null;
  parcelDetailsLoading: boolean;
  parcelDetailsError: any;
  displayUnit: 'm' | 'ft';
  setDisplayUnit: (unit: 'm' | 'ft') => void;
  interactionType: 'mapClick' | 'addressSearch';
  point: { lat: number, lng: number, type: 'mapClick' | 'addressSearch', address?: string } | null;
  parcelId?: string | undefined;
}

const SidePanel: React.FC<SidePanelProps> = ({
  selectedZone,
  userRole,
  parcelData,
  detailedParcelData,
  parcelDetailsLoading,
  parcelDetailsError,
  displayUnit,
  setDisplayUnit,
  interactionType,
  point,
  parcelId
}) => {

  // Memoize ZoneInfo props to prevent unnecessary re-renders
  const zoneInfoProps = useMemo(() => {
    const baseZoneProps = selectedZone || parcelData;
    const currentParcelId = parcelId || parcelData?.parceli2 || selectedZone?.PARCELI2;
    const currentParcelArea = parcelData?.lot_area_ft2;

    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.log('🔍 SidePanel - Preparing ZoneInfo props:', {
        hasSelectedZone: !!selectedZone,
        hasDetailedParcelData: false,
        hasParcelData: !!parcelData,
        hasBaseZoneProps: !!baseZoneProps,
        interactionType,
        hasPoint: !!point,
        currentParcelId,
        currentParcelArea
      });
    }

    return {
      zoneProperties: baseZoneProps,
      interactionType: interactionType,
      point: point,
      parcelId: currentParcelId,
      parcelAreaFt2: currentParcelArea,
      displayUnit: displayUnit,
    };
  }, [selectedZone, parcelData, parcelId, interactionType, point, displayUnit]);



  // Enhanced coordinates section with modern styling
  const renderCoordinates = () => {
    if (!point) return null;

    return (
      <div className="panel-card">
        <div className="panel-card-header">
          <div className="panel-card-header-content">
            <h3 className="panel-card-title">Location</h3>
            <p className="panel-card-subtitle">
              {interactionType === 'mapClick' ? 'Map Click' : 'Address Search'}
            </p>
          </div>
        </div>

        <div className="panel-card-content">
          {/* Display searched address if available */}
          {point.address && point.type === 'addressSearch' && (
            <div className="address-display">
              <div className="address-label">Address</div>
              <div className="address-value">{point.address}</div>
            </div>
          )}

          <div className="coordinates-grid">
            <div className="coordinate-item">
              <div className="coordinate-label">Latitude</div>
              <div className="coordinate-value">{point.lat.toFixed(6)}</div>
            </div>
            <div className="coordinate-item">
              <div className="coordinate-label">Longitude</div>
              <div className="coordinate-value">{point.lng.toFixed(6)}</div>
            </div>
          </div>

          <div className="status-indicator status-success">
            <span className="status-icon">✓</span>
            {point.type === 'addressSearch' ? 'Address Search' : 'Map Click'}
          </div>
        </div>
      </div>
    );
  };

  // Auth context for user state
  const { user } = useAuth();
  
  // Tab state
  const [activeTab, setActiveTab] = useState<'zoning' | 'analysis'>('zoning');
  
  // Handle tab change
  const handleTabChange = (tab: 'zoning' | 'analysis') => {
    setActiveTab(tab);
  };
  
  // Touch event isolation to prevent unwanted scrolling
  const handleTouchStart = (e: React.TouchEvent) => {
    // Allow normal scrolling within the panel but prevent bubbling
    e.stopPropagation();
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    // Allow normal scrolling within the panel but prevent bubbling
    e.stopPropagation();
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    // Allow normal scrolling within the panel but prevent bubbling
    e.stopPropagation();
  };
  
  return (
    <div 
      className="panel-content"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <TabNavigation 
        activeTab={activeTab} 
        onTabChange={handleTabChange} 
        user={user}
      />
      
      {/* Property tab content */}
      <div 
        className={`panel-section ${activeTab === 'zoning' ? 'active' : 'inactive'}`}
      >
        <ZoneInfo {...zoneInfoProps} />
        {renderCoordinates()}
      </div>

      {/* Analysis tab content */}
      <div 
        className={`panel-section ${activeTab === 'analysis' ? 'active' : 'inactive'}`}
      >
        <Suspense fallback={<div className="panel-card"><div className="panel-card-content">Loading analysis…</div></div>}>
          <AIAnalysis 
            zoneProperties={selectedZone || parcelData} 
            point={point}
          />
        </Suspense>
      </div>
    </div>
  );
};

export default React.memo(SidePanel);
